import type { SxProps, Theme } from "@mui/material/styles";
import { VideoBreakpoint } from "./HeroSection.config";

// Hero container - positioned within the combined sticky section
export const heroContainerSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  maxHeight: { xs: "560px", sm: "600px", md: "900px" },
  minHeight: { xs: "70%", xxl: "80%" },
  flex: "1 1 auto",
  // minHeight: 0,
};

// Animation container with border-radius and responsive dimensions
export const animationContainerSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  height: "100%",
  // Use clip-path instead of overflow:hidden to prevent edge flickering
  clipPath: "inset(0 round 1rem)",
  // Force GPU layer and anti-aliasing to prevent subpixel artifacts in Chrome
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  filter: "blur(0)",
  isolation: "isolate",
};

export const backgroundSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
};

// Layer 1: Full video without mask
export const fullVideoContainerSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  zIndex: 1,
};

// Clipping container for masked video (same dimensions as animation container)
export const maskedClipContainerSx: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) translateZ(0)",
  width: "100%",
  maxWidth: "1280px",
  height: "100%",
  zIndex: 2,
  pointerEvents: "none",
  // Use clip-path instead of overflow:hidden to prevent edge flickering
  clipPath: {
    xs: "inset(1px round 2rem)",
    md: "inset(1px round 3.5rem)",
  },
  // Prevent subpixel flickering during scale animation
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
};

// Layer 2: Wrapper for masked video (handles scale transform)
// Initial opacity: 0 prevents flash before GSAP initializes
export const maskedVideoWrapperSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transformOrigin: "center center",
  opacity: 0,
  // Prevent subpixel flickering during scale animation in Chrome
  willChange: "transform, opacity",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  // Force anti-aliasing and isolate compositing layer
  filter: "blur(0)",
  isolation: "isolate",
};

// Layer 2: Video container with clip-path mask
export const maskedVideoContainerSx: SxProps<Theme> = {
  width: "100%",
  aspectRatio: "481 / 471", // Match monogram.svg viewBox ratio
  clipPath: "url(#cledar-logo-clip)",
  // Prevent edge artifacts during animation
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
};

// Video base style
export const videoSx: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: { xs: "2rem", md: "3.5rem" },
};

// Full video (under poster) - needs absolute positioning
export const fullVideoSx: SxProps<Theme> = {
  ...videoSx,
  position: "absolute",
  inset: 0,
  zIndex: 0,
};

// Poster base style - covers video until it's ready
const posterBaseSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: { xs: "2rem", md: "3.5rem" },
  zIndex: 1,
  transition: "opacity 0.5s ease-in-out",
};

// Single poster style function - CSS controls display, JS controls opacity
export const getPosterSx = (
  breakpoint: VideoBreakpoint,
  isHidden: boolean
): SxProps<Theme> => ({
  ...posterBaseSx,
  display: {
    xs: breakpoint === VideoBreakpoint.Mobile ? "block" : "none",
    sm: breakpoint === VideoBreakpoint.Tablet ? "block" : "none",
    xl: breakpoint === VideoBreakpoint.Desktop ? "block" : "none",
  },
  opacity: isHidden ? 0 : 1,
  pointerEvents: isHidden ? "none" : "auto",
});

export const textOverlaySx: SxProps<Theme> = {
  position: "absolute",
  zIndex: 3,
  left: { xs: "1.5rem", sm: "2.5rem", md: "3.5rem", lg: "5rem" },
  bottom: { xs: "1.5rem", sm: "2.5rem", md: "3.5rem", lg: "5rem" },
  maxWidth: { xs: "18rem", sm: "20rem", md: "22rem", lg: "25rem" },
};

export const textTitleSx: SxProps<Theme> = {
  color: "white",
  fontWeight: 400,
  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.625rem", lg: "1.875rem" },
  lineHeight: 1.25,
  textShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
};
